import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {useGetSolutionDrugs} from '@/modules/drug-catalog/solutions/api'
import {MedicineSchema, TherapySequenceItemSchema} from '@/modules/protocols/schemas'
import {getFirstElementOrSelf} from '@cypro/ui/utils'
import {Box, Button, chakra, Text} from '@chakra-ui/react'
import {SolutionType} from '@cypro/engine/queries'
import {BasicDataResources} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {LuQuote, LuTrash2} from 'react-icons/lu'
import {DrugCatalogItem} from '@cypro/engine/schema'
import {timeDiffLabel} from '@/modules/patients/functions'
import {changeDoseUnitLabel} from '@/modules/protocols/functions'
import {useGetAllActiveSubstance} from '@/modules/drug-catalog/substances/api/use-get-all-active-substances'
import {useGetPrescribedNames} from '@/modules/products/trade-names/api'

interface Props {
  sequences: TherapySequenceItemSchema[]
  onRemoveSequence?: (id: string) => void
  onEditSequence?: (id: string) => void
  showDelete?: boolean
}

export function TherapySequenceTable({sequences, onRemoveSequence, onEditSequence, showDelete}: Props) {
  const {t} = useTranslate('common')
  const sortedSequences = sequences.toSorted((a, b) => {
    if (a.day !== b.day) return a.day - b.day

    const [aHours = 0, aMinutes = 0] = a.time.split(':').map(part => Number(part) || 0)
    const [bHours = 0, bMinutes = 0] = b.time.split(':').map(part => Number(part) || 0)

    const aTotal = aHours * 60 + aMinutes
    const bTotal = bHours * 60 + bMinutes

    return aTotal - bTotal
  })
  const [activeSubstances] = useGetAllActiveSubstance()
  const {prescribedNames} = useGetPrescribedNames()
  const {solutions: carrierSolutions} = useGetSolutionDrugs({filters: {types: [SolutionType.enum.CARRIER_SOLUTION]}})
  const {items: applicationForms} = useGetBasicDataItems({resource: BasicDataResources.applicationForms})

  const getMedicineValues = (medicine: MedicineSchema) => {
    let activeSubstanceId = getFirstElementOrSelf(medicine.activeSubstanceId)
    const prescribedNameId = getFirstElementOrSelf(medicine.prescribedNameId)
    const prescribedName = prescribedNames.find(pn => pn.id === prescribedNameId)
    if (!activeSubstanceId && prescribedName) {
      activeSubstanceId = prescribedName?.drugCatalogItemId || activeSubstanceId
    }

    const activeSubstance = activeSubstances.find(substance => substance.id === activeSubstanceId) || null
    const calculationMethod = getFirstElementOrSelf(medicine.calculationMethod)
    const regularDose = medicine.regularDose
    return {
      activeSubstance: activeSubstance as DrugCatalogItem,
      calculationMethod,
      regularDose,
      name: prescribedName?.name || activeSubstance?.name || null,
    }
  }

  return (
    <Box width="100%">
      <chakra.div display="flex" borderBottomWidth="1px" borderColor="border">
        <chakra.div
          padding="3"
          display="flex"
          alignItems="center"
          fontSize="sm"
          fontWeight="medium"
          color="fg.subtle"
          flex="1 1 0%"
          className="group"
          style={{
            maxWidth: '100px',
            minWidth: '100px',
          }}
        >
          {t('therapy_sequence.day')}
        </chakra.div>
        <chakra.div
          padding="3"
          display="flex"
          alignItems="center"
          fontSize="sm"
          fontWeight="medium"
          color="fg.subtle"
          flex="1 1 0%"
          className="group"
          style={{
            maxWidth: '100px',
            minWidth: '100px',
          }}
        >
          {t('therapy_sequence.time')}
        </chakra.div>
        <chakra.div
          padding="3"
          display="flex"
          alignItems="center"
          fontSize="sm"
          fontWeight="medium"
          color="fg.subtle"
          flex="1 1 0%"
          width="100%"
          className="group"
        >
          {t('therapy_sequence.table.medication', 'Medication')}
        </chakra.div>
        <chakra.div
          padding="3"
          display="flex"
          alignItems="center"
          fontSize="sm"
          fontWeight="medium"
          color="fg.subtle"
          flex="1 1 0%"
          width="100%"
          className="group"
        >
          {t('therapy_sequence.table.carrier', 'Carrier')}
        </chakra.div>
        <chakra.div
          padding="3"
          display="flex"
          alignItems="center"
          fontSize="sm"
          fontWeight="medium"
          color="fg.subtle"
          flex="1 1 0%"
          width="100%"
          className="group"
        >
          {t('therapy_sequence.table.administration', 'Administration')}
        </chakra.div>
        {showDelete && (
          <chakra.div
            padding="3"
            display="flex"
            alignItems="center"
            fontSize="sm"
            fontWeight="medium"
            color="fg.subtle"
            flex="0 0 auto"
            width="48px"
            className="group"
          ></chakra.div>
        )}
      </chakra.div>
      {sortedSequences.map((sequence, index) => {
        const {activeSubstance, calculationMethod, regularDose, name} = getMedicineValues(sequence.medicine[0] || {})
        const carrierSolutionId = getFirstElementOrSelf(sequence.carrierSolutionId)
        const applicationFormId = getFirstElementOrSelf(sequence.applicationFormId)
        const volumeMode = getFirstElementOrSelf(sequence.volumeMode)
        const timeLabel =
          sortedSequences[index + 1] && sortedSequences[index + 1].day === sequence.day
            ? timeDiffLabel(sequence.time, sortedSequences[index + 1].time)
            : undefined
        const secondMedicine = sequence.medicine[1]
        const {
          activeSubstance: secondActiveSubstance,
          calculationMethod: secondCalculationMethod,
          regularDose: secondRegularDose,
          name: secondName,
        } = getMedicineValues(secondMedicine || {})
        return (
          <chakra.div
            key={sequence.id}
            display="flex"
            flexDirection={'column'}
            borderBottomWidth="1px"
            borderColor="border"
            _hover={{bg: 'bg.muted'}}
            cursor={onEditSequence ? 'pointer' : 'default'}
            onClick={() => onEditSequence?.(sequence.id)}
          >
            <chakra.div display="flex">
              <chakra.div
                padding="3"
                display="flex"
                alignItems="center"
                fontSize="sm"
                fontWeight="medium"
                color="fg.subtle"
                flex="1 1 0%"
                style={{
                  maxWidth: '100px',
                  minWidth: '100px',
                }}
                className="group"
              >
                {sequence.day}
              </chakra.div>
              <chakra.div
                padding="3"
                display="flex"
                alignItems="center"
                fontSize="sm"
                fontWeight="medium"
                color="fg.subtle"
                flex="1 1 0%"
                style={{
                  maxWidth: '100px',
                  minWidth: '100px',
                }}
                className="group"
              >
                <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                  <Text fontWeight="normal" fontSize="sm" color="black">
                    {sequence.time}
                  </Text>
                  {timeLabel && (
                    <Text fontWeight="medium" fontSize="xs" color={'fg.muted'}>
                      {timeLabel}
                    </Text>
                  )}
                </Box>
              </chakra.div>
              {sequence.isNote === true ? (
                <chakra.div
                  padding="3"
                  display="flex"
                  alignItems="center"
                  fontSize="sm"
                  fontWeight="medium"
                  color="fg.subtle"
                  flex="1 1 0%"
                  width="100%"
                  className="group"
                >
                  <Box display={'flex'} flexDirection={'row'} gap={2}>
                    <LuQuote color="black" />
                    <Text fontWeight="medium" fontStyle="italic" fontSize="sm" color="fg.muted">
                      {sequence.note}
                    </Text>
                  </Box>
                </chakra.div>
              ) : (
                <>
                  <chakra.div
                    padding="3"
                    display="flex"
                    alignItems="center"
                    fontSize="sm"
                    fontWeight="medium"
                    color="fg.subtle"
                    flex="1 1 0%"
                    width="100%"
                    className="group"
                  >
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                      <Text fontWeight="normal" fontSize="sm" color="black">
                        {name || t('not_available', 'N/A')}
                      </Text>
                      <Text>
                        {calculationMethod ? t(calculationMethod) : '-'} {regularDose}{' '}
                        {changeDoseUnitLabel(calculationMethod, activeSubstance) || '-'}
                      </Text>
                    </Box>
                  </chakra.div>
                  <chakra.div
                    padding="3"
                    display="flex"
                    alignItems="center"
                    fontSize="sm"
                    fontWeight="medium"
                    color="fg.subtle"
                    flex="1 1 0%"
                    width="100%"
                    className="group"
                  >
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                      <Text fontWeight="normal" fontSize="sm" color="black">
                        {carrierSolutions.find(solution => solution.id === carrierSolutionId)?.name ||
                          t('not_available', 'N/A')}
                      </Text>
                      <Text>
                        {volumeMode && t(volumeMode)} {sequence.volume} ml
                      </Text>
                    </Box>
                  </chakra.div>
                  <chakra.div
                    padding="3"
                    display="flex"
                    alignItems="center"
                    fontSize="sm"
                    fontWeight="medium"
                    color="fg.subtle"
                    flex="1 1 0%"
                    width="100%"
                    className="group"
                  >
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                      <Text fontWeight="normal" fontSize="sm" color="black">
                        {applicationForms.find(form => form.id === applicationFormId)?.name ||
                          t('not_available', 'N/A')}
                      </Text>
                      <Text>{sequence.duration} min</Text>
                    </Box>
                  </chakra.div>
                </>
              )}

              {showDelete && (
                <chakra.div
                  padding="3"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="sm"
                  fontWeight="medium"
                  color="fg.subtle"
                  flex="0 0 auto"
                  width="48px"
                  className="group"
                >
                  {onRemoveSequence && (
                    <Button
                      variant="ghost"
                      size="sm"
                      colorPalette="red"
                      onClick={e => {
                        e.stopPropagation()
                        onRemoveSequence(sequence.id)
                      }}
                    >
                      <LuTrash2 />
                    </Button>
                  )}
                </chakra.div>
              )}
            </chakra.div>
            {secondMedicine && (
              <chakra.div
                padding="3"
                display="flex"
                alignItems="center"
                fontSize="sm"
                fontWeight="medium"
                color="fg.subtle"
                flex="1 1 0%"
                width="100%"
                className="group"
              >
                <Box display={'flex'} flexDirection={'column'} gap={'4px'} marginLeft="200px">
                  <Text fontWeight="normal" fontSize="sm" color="black">
                    {secondName || t('not_available', 'N/A')}
                  </Text>
                  <Text>
                    {secondCalculationMethod ? t(secondCalculationMethod) : '-'} {secondRegularDose}{' '}
                    {changeDoseUnitLabel(secondCalculationMethod, secondActiveSubstance) || '-'}
                  </Text>
                </Box>
              </chakra.div>
            )}
            {sequence.therapyNote && (
              <chakra.div
                padding="3"
                display="flex"
                alignItems="center"
                fontSize="sm"
                fontWeight="medium"
                color="fg.subtle"
                flex="1 1 0%"
                width="100%"
                className="group"
              >
                <Box display={'flex'} flexDirection={'row'} gap={2} marginLeft="200px">
                  <LuQuote color="black" />
                  <Text fontWeight="medium" fontStyle="italic" fontSize="sm" color="fg.muted">
                    {sequence.therapyNote}
                  </Text>
                </Box>
              </chakra.div>
            )}
          </chakra.div>
        )
      })}
    </Box>
  )
}
